import Box from "@mui/material/Box"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import TextField from "@mui/material/TextField"
import Switch from "@mui/material/Switch"

// ----------------------------------------------------------------------

export type CreditTypeConfig = {
    credits: number
    pulseRateMinsPerCredit: string
    maxCreditsPerCall: string
    pulse: number
    proctoring: boolean
    cefr: boolean
}

export const DEFAULT_CREDIT_TYPE_CONFIG: CreditTypeConfig = {
    credits: 100,
    pulseRateMinsPerCredit: "7",
    maxCreditsPerCall: "2",
    pulse: 7,
    proctoring: false,
    cefr: false,
}

export type CreditTypeKind = "video" | "audio" | "outboundAudio"

const COPY: Record<CreditTypeKind, { configTitle: string; creditsLabel: string }> = {
    video: {
        configTitle: "Video Configuration",
        creditsLabel: "Video Credits",
    },
    audio: {
        configTitle: "Audio Configuration",
        creditsLabel: "Audio Credits",
    },
    outboundAudio: {
        configTitle: "Outbound Audio Configuration",
        creditsLabel: "Outbound Audio Credits",
    },
}

// ----------------------------------------------------------------------

export interface CreditTypeConfigSectionProps {
    type: CreditTypeKind
    config: CreditTypeConfig
    onChange: (config: CreditTypeConfig) => void
    errors?: Partial<Record<keyof CreditTypeConfig, string>>
}

export function CreditTypeConfigSection({ type, config, onChange, errors }: CreditTypeConfigSectionProps) {
    const copy = COPY[type]
    const showProctoring = type === "video"

    const update = (field: keyof CreditTypeConfig, value: string | boolean) => {
        onChange({ ...config, [field]: value })
    }

    return (
        <Box
            sx={{
                pl: 2,
                pr: 2,
                pt: 2,
                pb: 2,
                borderRadius: 1,
                border: 1,
                borderColor: "divider",
                bgcolor: "background.paper",
            }}
        >
            <Typography
                variant="overline"
                sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    letterSpacing: 1,
                    display: "block",
                    mb: 2,
                }}
            >
                {copy.configTitle}
            </Typography>
            <Stack spacing={2}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                        fullWidth
                        label={copy.creditsLabel}
                        type="number"
                        value={config.credits}
                        onChange={(e) => update("credits", e.target.value)}
                        size="small"
                        inputProps={{ min: 0 }}
                        error={Boolean(errors?.credits)}
                        helperText={errors?.credits}
                    />
                    <TextField
                        fullWidth
                        label="Pulse Rate (mins/credit)"
                        type="number"
                        value={config.pulseRateMinsPerCredit}
                        onChange={(e) => update("pulseRateMinsPerCredit", e.target.value)}
                        size="small"
                        inputProps={{ min: 0 }}
                        error={Boolean(errors?.pulseRateMinsPerCredit)}
                        helperText={errors?.pulseRateMinsPerCredit}
                    />
                </Stack>
                <TextField
                    fullWidth
                    label="Max Credits per Call"
                    type="number"
                    value={config.maxCreditsPerCall}
                    onChange={(e) => update("maxCreditsPerCall", e.target.value)}
                    size="small"
                    inputProps={{ min: 0 }}
                    error={Boolean(errors?.maxCreditsPerCall)}
                    helperText={errors?.maxCreditsPerCall}
                />
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: "grey.50",
                        border: 1,
                        borderColor: "divider",
                    }}
                >
                    <Box>
                        <Typography variant="subtitle2">CEFR Enabled</Typography>
                        <Typography variant="caption" color="text.secondary">
                            Enable CEFR language evaluation
                        </Typography>
                    </Box>
                    <Switch checked={config.cefr} onChange={(e) => update("cefr", e.target.checked)} color="primary" />
                </Stack>
                {showProctoring && (
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                            p: 1.5,
                            borderRadius: 1,
                            bgcolor: "grey.50",
                            border: 1,
                            borderColor: "divider",
                        }}
                    >
                        <Box>
                            <Typography variant="subtitle2">Proctored Mode</Typography>
                            <Typography variant="caption" color="text.secondary">
                                Enable anti-cheating measures
                            </Typography>
                        </Box>
                        <Switch
                            checked={config.proctoring}
                            onChange={(e) => update("proctoring", e.target.checked)}
                            color="primary"
                        />
                    </Stack>
                )}
            </Stack>
        </Box>
    )
}
